/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { forwardRef, useCallback, useState } from 'react';

import { PasswordField } from '@coursera/cds-core';
import { SvgEye, SvgHidden } from '@coursera/coursera-ui/svg';

import type { Props as InputProps } from 'bundles/authentication/shared/TextInput';
import TextInput from 'bundles/authentication/shared/TextInput';
import { commonStyles } from 'bundles/authentication/shared/commonStyles';
import GrowthDegreesExperiments from 'bundles/epic/clients/GrowthDegrees';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/authentication';

type OwnProps = {
  label?: string;
  name?: string;
  placeholder?: string;
  'data-e2e'?: string;
  autoComplete?: 'new-password' | 'current-password';
};

type Props = OwnProps &
  Omit<InputProps, keyof OwnProps | 'type'> & {
    value?: string | number;
    defaultValue?: string | number;
  };
const styles = {
  root: css`
    position: relative;

    input::-webkit-credentials-auto-fill-button {
      visibility: hidden;
      pointer-events: none;
      position: absolute;
      right: 0;
    }

    input::-ms-reveal,
    input::-ms-clear {
      display: none;
    }
  `,
  icon: css`
    background: transparent;
    border: 0;
    height: 46px;
    outline: none;
    width: 46px;

    &:focus {
      path {
        stroke: #2a73cc;
      }
    }

    @media (hover: hover) {
      &:hover path {
        stroke: #2a73cc;
      }
    }
  `,
};

const PasswordInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      label = _t('Password'),
      name = 'password',
      placeholder = _t('Create password'),
      autoComplete = 'current-password',
      ...props
    },
    ref
  ) => {
    const useCDSOnRegForm = GrowthDegreesExperiments.get('useCDSOnRegForm');
    const [hidden, setHidden] = useState(true);
    const toggle = useCallback(() => {
      setHidden((prev) => !prev);
    }, [setHidden]);
    const Icon = hidden ? SvgEye : SvgHidden;
    const aria = hidden ? _t('Your password is hidden') : _t('Your password is shown');

    return useCDSOnRegForm ? (
      <div>
        <PasswordField
          inputProps={{ 'data-e2e': props?.['data-e2e'] }}
          css={commonStyles.input}
          fullWidth
          label={label}
          name={name}
          placeholder={placeholder}
          ref={ref}
          autoComplete={autoComplete}
          {...{ ...props, 'data-e2e': undefined }}
        />
        {props?.hint && <div css={commonStyles.hint}>{props?.hint}</div>}
      </div>
    ) : (
      <TextInput
        {...props}
        label={label}
        name={name}
        placeholder={placeholder}
        ref={ref}
        suffix={
          <TrackedButton
            aria-live="polite"
            aria-label={aria}
            css={styles.icon}
            trackingName="show_password_eyeball"
            onClick={toggle}
          >
            <Icon title={hidden ? _t('Show password') : _t('Hide password')} />
          </TrackedButton>
        }
        type={hidden ? 'password' : 'text'}
        autoComplete={autoComplete}
      />
    );
  }
);
export default PasswordInput;
